import { useState, useEffect, useMemo, useCallback, useRef } from 'react'

import { 
  isDisplayable, 
  isProductAccessible }         from '../../../shared/utils/productUtils.js'

import {
  applyInitialFilteringOverlay,
  buildInitialActiveConfig,
  countActiveFilters,
  rebaseFiltersConfig }         from '../utils/filterUtils.js'
import { buildInitialFilters }  from '../utils/buildInitialFilters.js'
import { 
  areSortConfigsEqual, 
  buildInitialSorting }         from '../utils/buildInitialSorting.js'
import { filterProducts }       from '../utils/filterProducts.js'
import { FILTER_REGISTRY } from '../registries/filterRegistry.js'
import { 
  FILTER_IDS, 
  COMPONENT_TYPES }             from '../constants/filterConstants.js'


// ── Types ──────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} FiltersConfig
 * @property {Object} initialFiltering          - The initial filtering overlay (e.g. CMS defaults).
 * @property {Array} initialSortingLevels       - The initial sorting configuration levels.
 * @property {boolean} reservedUnitsAccessible  - Whether reserved units are accessible.
 * @property {boolean} soldUnitsAccessible      - Whether sold units are accessible.
 */

/**
 * @typedef {Object} UseFiltersResult
 * @property {Object|null} activeFiltersConfig  - The currently applied filters configuration.
 * @property {number} activeFiltersCount        - Number of active filters differing from defaults.
 * @property {boolean} canRestorePreReset       - True if a pre-reset snapshot is available.
 * @property {Array} catalogProducts            - Products filtered by displayability and accessibility.
 * @property {Object|null} defaultFiltersConfig - The CMS default filters configuration.
 * @property {Array} filteredProducts           - Products after applying active filters and sorting.
 * @property {Object|null} fullFiltersRange     - The baseline of all possible filter values.
 * @property {Function} resetFilters            - Resets filters to default configurations.
 * @property {Function} resetSortConfig         - Resets sorting to initial CMS state.
 * @property {Function} restorePreReset         - Restores filters to the state before the last reset.
 * @property {Function} setActiveFiltersConfig  - Setter for active filters (accepts object or updater function).
 * @property {Function} setSortConfig           - Setter for sort configuration.
 * @property {Array} sortConfig                 - The current sorting configuration.
 */

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Manages filter state, sort configuration, and the derived filtered product list
 * for the catalog domain.
 *
 * Two-phase lifecycle:
 *   1. While products are loading → filterRegistry and configs are null/empty.
 *   2. Once displayableProducts is non-empty → fullFiltersRange and
 *      defaultFiltersConfig are computed and activeFiltersConfig is initialized.
 *
 * Naming:
 *   - `fullFiltersRange`    — raw buildInitialFilters output; every possible value/range.
 *                            Passed to filterProducts as the skip-rule baseline.
 *   - `defaultFiltersConfig` — fullFiltersRange + config overlay (CMS intent).
 *                            What "reset" returns to. Exposed to consumers.
 *
 * Reset behavior:
 *   - `resetFilters` restores `defaultFiltersConfig` (config overlay applied),
 *     NOT the full `fullFiltersRange`. A snapshot of the pre-reset state is saved
 *     for one-shot undo via `restorePreReset`.
 *   - Any manual filter change after a reset clears the snapshot automatically.
 *
 * Note: `compartments` should be a stable object reference (e.g. from useState or
 * useMemo in the caller). Changing it rebuilds `fullFiltersRange`.
 *
 * @param {Object}         params
 * @param {Array}          params.buildings    — buildings array from useProducts
 * @param {Function}      [params.isFavorite=() => false] — (uuid: string) => boolean; for favorites sort
 * @param {FiltersConfig}  params.config       — configuration object mapped from capabilities & CMS
 * @param {Object|Array}  [params.compartments={}] — compartments.json data (optional)
 * @param {Array}          params.products     — all products from useProducts
 * @returns {UseFiltersResult}
 */
export function useFilters({
  buildings, 
  isFavorite = () => false,
  config = {}, 
  compartments = {}, 
  products, 
}) {
  // ── Derived stable data ───────────────────────────────────────────────────

  const {
    initialFiltering,
    initialSortingLevels,
    reservedUnitsAccessible,
    soldUnitsAccessible,
  } = config;

  const catalogProducts = useMemo(
    () => products.filter((product) => {
      return isDisplayable(product.visibility)
    }),
    [products, reservedUnitsAccessible, soldUnitsAccessible]
  )

  // Full-range baseline: every product passes when active config equals this.
  // Passed to filterProducts as defaultFiltersConfig (skip-rule reference).
  const fullFiltersRange = useMemo(
    () => {
      if (catalogProducts.length === 0) return null
      return buildInitialFilters({ products: catalogProducts, buildings, compartments })
    },
    [catalogProducts, buildings, compartments]
  )

  // CMS default: array MultiSelect values start empty (empty = show all) + panelSettings overlay.
  // This is what "Reset" returns to. activeFiltersCount measures drift from here.
  const defaultFiltersConfig = useMemo(
    () => {
      if (!fullFiltersRange) return null
      const multiSelectIds = new Set(
        FILTER_REGISTRY.filter(e => e.component === COMPONENT_TYPES.MULTI_SELECT).map(e => e.id)
      )
      const base = buildInitialActiveConfig(fullFiltersRange, multiSelectIds)
      return applyInitialFilteringOverlay(
        base,
        initialFiltering
      )
    },
    [fullFiltersRange, FILTER_REGISTRY, initialFiltering]
  )

  const initialSortConfig = useMemo(
    () => buildInitialSorting(initialSortingLevels),
    [initialSortingLevels]
  )

  // ── State ─────────────────────────────────────────────────────────────────

  const [activeFiltersConfig, setActiveFiltersConfigRaw] = useState(null)
  const [sortConfig, setSortConfig] = useState(initialSortConfig)
  const [preResetSnapshot, setPreResetSnapshot] = useState(null)
  const prevDefaultFiltersConfigRef = useRef(null)
  const prevInitialSortConfigRef = useRef(initialSortConfig)

  // Keep the active config aligned with newer computed defaults without erasing
  // user changes.
  useEffect(() => {
    if (!defaultFiltersConfig) return

    setActiveFiltersConfigRaw((prev) => {
      return rebaseFiltersConfig(prev, prevDefaultFiltersConfigRef.current, defaultFiltersConfig)
    })

    prevDefaultFiltersConfigRef.current = defaultFiltersConfig
  }, [defaultFiltersConfig])

  // Re-sync initial sort config when CMS input changes, but only while the
  // current state still matches the previous initial chain.
  useEffect(() => {
    const previousInitialSortConfig = prevInitialSortConfigRef.current

    setSortConfig((prev) => {
      if (!prev || areSortConfigsEqual(prev, previousInitialSortConfig)) {
        return initialSortConfig
      }
      return prev
    })

    prevInitialSortConfigRef.current = initialSortConfig
  }, [initialSortConfig])

  // ── Actions ───────────────────────────────────────────────────────────────

  /**
   * Public setter for active filters. Clears the undo-reset snapshot on any
   * manual change so `restorePreReset` is only available immediately after reset.
   *
   * Automatically mirrors price_range ↔ price_input: they share the same value
   * concept (brutto price range), so a change to either propagates to the other.
   */
  const setActiveFiltersConfig = useCallback((update) => {
    if (preResetSnapshot !== null) setPreResetSnapshot(null)
    setActiveFiltersConfigRaw(prev => {
      const next = typeof update === 'function' ? update(prev) : update
      // Mirror linked price filters so slider and inputs always stay in sync
      const priceRangeChanged = next[FILTER_IDS.PRICE_RANGE] !== prev?.[FILTER_IDS.PRICE_RANGE]
      const priceInputChanged = next[FILTER_IDS.PRICE_INPUT] !== prev?.[FILTER_IDS.PRICE_INPUT]
      if (priceRangeChanged && !priceInputChanged) {
        return { ...next, [FILTER_IDS.PRICE_INPUT]: next[FILTER_IDS.PRICE_RANGE] }
      }
      if (priceInputChanged && !priceRangeChanged) {
        return { ...next, [FILTER_IDS.PRICE_RANGE]: next[FILTER_IDS.PRICE_INPUT] }
      }
      return next
    })
  }, [preResetSnapshot])

  /**
   * Resets filters to `defaultFiltersConfig` (the panelSettings overlay / CMS default).
   * Saves a snapshot of the current state for one-shot undo via `restorePreReset`.
   */
  const resetFilters = useCallback(() => {
    if (!defaultFiltersConfig) return
    setPreResetSnapshot(activeFiltersConfig)
    setActiveFiltersConfigRaw(defaultFiltersConfig)
  }, [activeFiltersConfig, defaultFiltersConfig])

  /**
   * Resets sort configuration to the initial CMS-configured state.
   * Does not affect filter state.
   */
  const resetSortConfig = useCallback(() => {
    setSortConfig(initialSortConfig)
  }, [initialSortConfig])

  /**
   * Restores the pre-reset snapshot (one-shot). Clears the snapshot after use.
   * No-op if no snapshot is available.
   */
  const restorePreReset = useCallback(() => {
    if (!preResetSnapshot) return
    setActiveFiltersConfigRaw(preResetSnapshot)
    setPreResetSnapshot(null)
  }, [preResetSnapshot])

  // ── Derived ───────────────────────────────────────────────────────────────

  const filteredProducts = useMemo(() => {
    if (!activeFiltersConfig || !fullFiltersRange) return catalogProducts
    return filterProducts({
      products: catalogProducts,
      filtersConfig: activeFiltersConfig,
      defaultFiltersConfig: fullFiltersRange,
      sortConfig,
      isFavorite,
    })
  }, [catalogProducts, activeFiltersConfig, fullFiltersRange, sortConfig, isFavorite])

  // Ids of filters that are UI mirrors and should not count toward the badge.
  const countSkipIds = useMemo(
    () => new Set(FILTER_REGISTRY.filter(e => e.skipActiveCount).map(e => e.id)),
    [FILTER_REGISTRY]
  )

  // Count of filters that differ from the CMS default (not the full range).
  const activeFiltersCount = useMemo(() => {
    if (!activeFiltersConfig || !defaultFiltersConfig) return 0
    return countActiveFilters(activeFiltersConfig, defaultFiltersConfig, countSkipIds)
  }, [activeFiltersConfig, defaultFiltersConfig, countSkipIds])

  // ── Return ────────────────────────────────────────────────────────────────

  return {
    activeFiltersConfig,
    activeFiltersCount,
    canRestorePreReset: preResetSnapshot !== null,
    catalogProducts,
    defaultFiltersConfig,
    filteredProducts,
    fullFiltersRange,
    setActiveFiltersConfig,
    setSortConfig,
    sortConfig,
    resetFilters,
    resetSortConfig,
    restorePreReset,
  }
}
